import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/config";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Loader2, Inbox, Send, Phone, MessageSquare, X, Copy, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/**
 * iter61 — Admin section listing in-flight (incomplete) bookings + KPIs.
 * Actions per row: Copy Resume Link · Text · Call · Resend · Dismiss.
 */
export default function IncompleteBookingsPanel() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: list }, { data: st }] = await Promise.all([
        api.get("/admin/incomplete-bookings?limit=100"),
        api.get("/admin/incomplete-bookings/stats"),
      ]);
      setRows(list?.attempts || []);
      setStats(st || null);
    } catch (_e) { setRows([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const copyLink = (a) => {
    const link = a.resume_link || `${window.location.origin}/book?resume=${a.resume_token}`;
    navigator.clipboard.writeText(link).then(
      () => toast.success("Continue-booking link copied"),
      () => toast.error("Copy failed — try again"),
    );
  };
  const resend = async (a) => {
    setBusy(a.attempt_id);
    try {
      await api.post(`/admin/incomplete-bookings/${a.attempt_id}/resend`);
      toast.success("Reminder sent");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resend");
    } finally { setBusy(null); }
  };
  const dismiss = async (a) => {
    setBusy(a.attempt_id);
    try {
      await api.post(`/admin/incomplete-bookings/${a.attempt_id}/dismiss`);
      toast.success("Dismissed");
      refresh();
    } catch (_e) { toast.error("Could not dismiss"); }
    finally { setBusy(null); }
  };

  return (
    <Card className="rounded-3xl" data-testid="incomplete-bookings-panel">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-amber-700 font-bold">Recovery</div>
            <h3 className="text-xl font-extrabold tracking-tight text-gray-900 font-[Outfit]">
              Incomplete Bookings
            </h3>
            <p className="text-[12px] text-gray-600 mt-0.5">
              Customers who started but didn&apos;t finish — nudge them back.
            </p>
          </div>
          {stats && (
            <div className="grid grid-cols-3 gap-3 text-center" data-testid="incomplete-bookings-stats">
              <div><div className="text-xl font-extrabold text-gray-900">{stats.pending}</div><div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Pending</div></div>
              <div><div className="text-xl font-extrabold text-emerald-700">{stats.recovered}</div><div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Recovered</div></div>
              <div><div className="text-xl font-extrabold text-violet-700">{stats.recovery_rate}%</div><div className="text-[10px] uppercase tracking-wider text-violet-700 font-bold">Rate</div></div>
            </div>
          )}
        </div>

        <div className="mt-4 divide-y divide-gray-100">
          {loading && <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-emerald-700" /></div>}
          {!loading && rows.length === 0 && (
            <div className="py-8 text-center text-gray-500" data-testid="incomplete-empty">
              <Inbox className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm font-semibold">No incomplete bookings — everyone finished 🎉</p>
            </div>
          )}
          {!loading && rows.map((a) => (
            <div key={a.attempt_id} className="py-3 flex flex-wrap items-center gap-3"
                 data-testid={`incomplete-row-${a.attempt_id}`}>
              <div className="flex-1 min-w-[220px]">
                <div className="text-[13px] font-extrabold text-gray-900 truncate">
                  {a.name || "Anonymous"}
                  {a.recovery_status === "started" && <span className="ml-2 text-[10px] font-bold text-emerald-700 uppercase tracking-wider">↺ returned</span>}
                </div>
                <div className="text-[11px] text-gray-600 truncate">
                  {a.service_name || a.service_key || "—"}
                  {a.price ? ` · $${Number(a.price).toFixed(2)}` : ""}
                  {a.formatted_address || a.address ? ` · ${a.formatted_address || a.address}` : ""}
                </div>
                <div className="text-[10px] text-gray-500 mt-0.5">
                  {a.email || "no email"} · {a.phone || "no phone"} · updated {new Date(a.updated_at).toLocaleString()}
                  {a.reminders_sent?.length ? ` · ${a.reminders_sent.length} reminder${a.reminders_sent.length === 1 ? "" : "s"}` : ""}
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                <Button size="sm" variant="outline" className="h-8 rounded-full text-[11px]" onClick={() => copyLink(a)}
                        data-testid={`incomplete-copy-${a.attempt_id}`}>
                  <Copy className="w-3 h-3 mr-1" /> Continue Booking
                </Button>
                {a.phone && (
                  <>
                    <Button size="sm" variant="outline" className="h-8 rounded-full text-[11px]" asChild
                            data-testid={`incomplete-call-${a.attempt_id}`}>
                      <a href={`tel:${a.phone}`}><Phone className="w-3 h-3 mr-1" /> Call</a>
                    </Button>
                    <Button size="sm" variant="outline" className="h-8 rounded-full text-[11px]" asChild
                            data-testid={`incomplete-text-${a.attempt_id}`}>
                      <a href={`sms:${a.phone}`}><MessageSquare className="w-3 h-3 mr-1" /> Text</a>
                    </Button>
                  </>
                )}
                <Button size="sm" onClick={() => resend(a)} disabled={busy === a.attempt_id}
                        className="h-8 rounded-full text-[11px] bg-emerald-700 hover:bg-emerald-800 text-white"
                        data-testid={`incomplete-resend-${a.attempt_id}`}>
                  {busy === a.attempt_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3 mr-1" />} Resend
                </Button>
                <Button size="sm" variant="ghost" onClick={() => dismiss(a)} disabled={busy === a.attempt_id}
                        className="h-8 rounded-full text-[11px] text-gray-500 hover:text-red-600"
                        data-testid={`incomplete-dismiss-${a.attempt_id}`}>
                  <X className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
